import React from 'react';
import childLegs from '../../../assets/images/theraputic_screen/child_legs.jpg';
import GradientOutlineButton from '../../../components/atoms/GradientOutlineButton';
import GradientButton from '../../../components/atoms/GradientButton';
import { motion } from 'framer-motion';
import ModalitiesSection from './ModalitiesSection';
import { useMediaQuery } from 'react-responsive';

const CommonlyTreated = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });

  const TitleAndDescription = ({ title, description }) => {
    return (
      <div className="flex-1 flex flex-col gap-2">
        <h2 className="text-[28px] sm:text-[34px] text-primary font-bold">
          {title}
        </h2>
        <p
          className="text-secondary text-lg 2xl:text-2xl"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>
    );
  };
  const servicesData = [
    {
      name: 'Physical Therapy Modalities',
      items: [
        'Infant massage',
        'Bilateral motor coordination training',
        'Infant head measurement',
        'Home exercise programs',
        'Gait training',
        'Play-based intervention',
        'Neuromuscular re-education',
        'Neuromuscular electrical stimulation',
        'Balance training',
        'Aerobic endurance training',
        'Stretching',
        'Neurodevelopmental treatment',
        'Kinesiotaping',
        'Myofascial release',
        'Instrument-assisted soft tissue mobilization',
        'Parent education',
        'Assistive device training',
        'Environmental adaptations',
        'Orthotic recommendation and patient education',
        'Postural re-education',
      ],
    },
  ];

  return (
    <div className="w-full flex flex-col justify-between gap-4 sm:gap-10 max-w-[1200px] mx-auto px-4 md:px-10 py-10 md:py-20 relative z-10">
      <div>
        <h1 className="text-[38px] sm:text-[40px] md:text-[54px] text-primary font-bold">
          Physical Therapy
        </h1>
        <h2 className="text-[30px] sm:text-[34px] md:text-[40px] text-primary font-bold">
          Commonly Treated Diagnoses
        </h2>
      </div>
      <div className="flex flex-col md:flex-row items-start gap-4 sm:gap-10 xl:gap-28">
        <TitleAndDescription
          title="Developmental Delays"
          description="Children can experience delayed development for a number of reasons, including early hospitalizations, sensory dysregulation, poor muscle strength, low muscle tone, balance trouble, and other diagnoses or medical conditions. Your child’s therapist will determine the degree of developmental delay, using research-backed standardized tests, and will develop a program to address the issue and get your child back to meeting their gross motor milestones."
        />
        <TitleAndDescription
          title="Toe Walking"
          description="Kids who walk on their toes more than 25% of the time may have some impairments in either ankle range of motion, abnormal tone in the legs, or sensory concerns. Therapists will determine the reason behind your child’s toe walking, and develop a unique therapy program to address impairments. Commonly seen issues associated with toe walking are core weakness, tightness in the ankle joint and/or leg muscles, increased muscle tone in the legs, trouble with balance, and sensory dysregulation. "
        />
      </div>
      <div className="flex flex-col md:flex-row items-start gap-4 sm:gap-10 xl:gap-28">
        <TitleAndDescription
          title="Down Syndrome"
          description="These children typically present with low muscle tone, muscle weakness, poor balance, and developmental delays. Therapists will utilize play-based treatments to encourage the child to improve their sitting and standing posture, leg strength, balance, and motor planning for activities including crawling, walking, jumping, etc., depending on the child’s age. "
        />
        <div className="flex-1">
          <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : 300 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.5 }}
          >
            <img
              src={childLegs}
              alt="Pediatric physical therapy with balance exercises in Wasilla"
              className="rounded-3xl w-full sm:w-[90%]"
            />
          </motion.div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row items-start gap-4 sm:gap-10 xl:gap-28">
        <TitleAndDescription
          title="Cerebral Palsy"
          description="Children who sustain an injury to the nervous system either in-utero or shortly after birth may present with increased muscle tone, abnormal developmental reflexes, and mobility issues. Therapists will address each child’s specific impairments and work with the family to provide any assistive equipment or orthotics, improve your child’s strength and posture, address mobility concerns in the home and school, and get your child back to their job- playing and exploring the world around them."
        />
        <TitleAndDescription
          title="Hypermobility Syndromes"
          description="Children with gross hypermobility may sustain frequent injuries, have poor muscle strength, fall or run into things, have poor balance, and/or have trouble with coordination and motor planning. These children benefit from an exercise program that will improve their muscle strength, balance, coordination, postural stability, and endurance, so they can engage in sports and activities with less concern of injuries or being held back from peers."
        />
      </div>
      <div>
        <motion.div
          initial={{ opacity: 0, x: isMobile ? -50 : -300 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          viewport={{ once: true, amount: 0.5 }}
        >
          <h2 className="text-[28px] md:text-[32px] text-primary font-semibold">
            ”We have LOVED our experience with physical therapy at Guidepost.
            Our daughter has benefited greatly from their treatment and
            compassion. They take the time to implement strategies very specific
            to our child and her sensory needs.”
          </h2>
        </motion.div>
      </div>
      <ModalitiesSection services={servicesData} />
      <motion.div
        initial={{ opacity: 0, x: -200 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.3, ease: 'easeInOut', delay: 0.3 }}
        viewport={{ once: true, amount: 0.3 }}
        className="flex items-center justify-center gap-4 sm:gap-10 flex-col md:flex-row mt-4"
      >
        <GradientOutlineButton
          text={'Learn More About Physical Therapy'}
          width={'w-full sm:w-[320px]'}
          navigateTo={'/services/physical-therapy'}
        />
        <a href="tel:+19073764880" className="w-full sm:w-[320px]">
          <GradientButton
            text={'Call to Schedule an Appointment'}
            width={'w-full sm:w-[320px]'}
          />
        </a>
      </motion.div>
    </div>
  );
};

export default CommonlyTreated;
